// Event-driven crosshair animation: envelopes, instance pooling, sampling.
//
// One `Fx` is a delta applied over time. Multiple triggers can overlap (LG
// grind, shotgun pellets) so each Fx owns a small pool of instances whose
// contributions are summed — resetting a single envelope at 15 Hz would look
// like a constant offset, not a pulse.

import {
  MAX_FX,
  clamp,
  mixHex,
  type CrosshairConfig,
  type Easing,
  type Fx,
  type Layer,
  type Trigger,
} from "./types";

export interface LayerAnim {
  scale: number;
  rotate: number;
  gap: number;
  thickness: number;
  offsetX: number;
  offsetY: number;
  opacity: number;
  color: string;
  outlineColor: string;
}

const EASE: Record<Easing, (t: number) => number> = {
  linear: (t) => t,
  out: (t) => 1 - Math.pow(1 - t, 3),
  in: (t) => t * t * t,
  inout: (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
  back: (t) => 1 + 2.70158 * Math.pow(t - 1, 3) + 1.70158 * Math.pow(t - 1, 2),
  elastic: (t) =>
    t <= 0 ? 0 : t >= 1 ? 1 : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * ((2 * Math.PI) / 3)) + 1,
};

const INSTANCE_CAP = 24;
const STAGGER_MS = 8;

function duration(fx: Fx): number {
  return Math.max(1, fx.attack + fx.hold + fx.release);
}

function envelope(age: number, fx: Fx): number {
  if (age < 0) return 0;
  const ease = EASE[fx.easing] ?? EASE.out;
  const a = fx.attack;
  const h = fx.hold;
  const r = fx.release;
  if (a > 0 && age < a) return ease(age / a);
  if (age < a + h) return 1;
  if (r > 0 && age < a + h + r) return 1 - ease((age - a - h) / r);
  // Zero-length envelope: one-frame flash.
  if (a + h + r <= 0) return age < 1 ? 1 : 0;
  return 0;
}

const paramNum = (layer: Layer, key: string, fallback: number): number => {
  const v = layer.params[key];
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
};

export class CrosshairAnimator {
  private cfg: CrosshairConfig;
  /** "layerIndex:fxIndex" -> instance start times. */
  private instances = new Map<string, number[]>();
  /** Cached trigger presence so idle frames cost almost nothing. */
  private triggers = new Set<Trigger>();
  /** Longest envelope in the config (upper bound for `active`). */
  private maxDuration = 1;

  constructor(cfg: CrosshairConfig) {
    this.cfg = cfg;
    this.rebuild();
  }

  setConfig(cfg: CrosshairConfig): void {
    this.cfg = cfg;
    this.instances.clear();
    this.rebuild();
  }

  private rebuild(): void {
    this.triggers.clear();
    this.maxDuration = 1;
    for (const layer of this.cfg.layers) {
      for (const fx of layer.fx.slice(0, MAX_FX)) {
        this.triggers.add(fx.trigger);
        this.maxDuration = Math.max(this.maxDuration, duration(fx));
      }
    }
  }

  hasTrigger(kind: Trigger): boolean {
    return this.triggers.has(kind);
  }

  fire(kind: Trigger, now: number, count = 1): void {
    if (!this.triggers.has(kind)) return;
    const n = Math.max(1, Math.min(8, Math.round(count)));
    this.cfg.layers.forEach((layer, li) => {
      layer.fx.forEach((fx, fi) => {
        if (fx.trigger !== kind) return;
        const key = `${li}:${fi}`;
        let list = this.instances.get(key);
        if (!list) {
          list = [];
          this.instances.set(key, list);
        }
        if (fx.retrigger === "ignore" && list.some((s) => envelope(now - s, fx) > 0)) return;
        if (fx.retrigger === "reset") list.length = 0;
        for (let i = 0; i < n; i++) list.push(now + i * STAGGER_MS);
        while (list.length > INSTANCE_CAP) list.shift();
      });
    });
  }

  reset(): void {
    this.instances.clear();
  }

  /** True while any instance is still contributing (call after `sample`). */
  active(now: number): boolean {
    for (const list of this.instances.values()) {
      for (const start of list) {
        if (now - start < this.maxDuration) return true;
      }
    }
    return false;
  }

  sample(now: number): LayerAnim[] {
    const out: LayerAnim[] = [];
    this.cfg.layers.forEach((layer, li) => {
      let scale = 1;
      let rotate = 0;
      let gap = paramNum(layer, "gap", 0);
      let thickness = paramNum(layer, "thickness", 2);
      let ox = 0;
      let oy = 0;
      let opacity = layer.opacity;
      let colorW = 0;
      let outlineW = 0;
      let color = layer.color;
      let outlineColor = layer.outline.color;

      layer.fx.forEach((fx, fi) => {
        const list = this.instances.get(`${li}:${fi}`);
        if (!list || !list.length) return;
        const d = duration(fx);
        for (let i = list.length - 1; i >= 0; i--) {
          const age = now - list[i];
          if (age > d + 1) {
            list.splice(i, 1);
            continue;
          }
          const p = envelope(age, fx);
          if (p <= 0) continue;
          scale += fx.scale * p;
          rotate += fx.rotate * p;
          gap += fx.gap * p;
          thickness += fx.thickness * p;
          ox += fx.offsetX * p;
          oy += fx.offsetY * p;
          opacity += fx.opacity * p;
          if (fx.color && p > colorW) {
            colorW = p;
            color = fx.color;
          }
          if (fx.outlineColor && p > outlineW) {
            outlineW = p;
            outlineColor = fx.outlineColor;
          }
        }
        if (!list.length) this.instances.delete(`${li}:${fi}`);
      });

      // Reset instanced values to the layer defaults, then apply the envelope.
      out.push({
        scale: Math.max(0.05, scale),
        rotate,
        gap: Math.max(0, gap),
        thickness: Math.max(0.2, thickness),
        offsetX: ox,
        offsetY: oy,
        opacity: clamp(opacity, 0, 1),
        color: mixHex(layer.color, color, colorW),
        outlineColor: mixHex(layer.outline.color, outlineColor, outlineW),
      });
    });
    return out;
  }
}
